/**
 * Warnung bei überfälligen Rechnungen: Banner ganz oben im Portal (wie beim Testbetrieb), sobald eine abgerechnete
 * Rechnung X Tage nach Rechnungsdatum nicht bezahlt ist. Global einstellbar (Logiful-Admins), je Kunde überschreibbar.
 * Lastschriften, die bei GoCardless noch laufen, zählen nicht als überfällig.
 * Optional „Aufträge pausieren“: ab einer späteren Grenze (Vorwarnzeit = Differenz) gehen neue Aufträge nicht mehr ans
 * Lager, bis die Rechnungen ausgeglichen sind; bereits übertragene laufen weiter.
 */
import { z } from "zod";

/** Wer beim Kunden das Banner sieht */
export const OVERDUE_AUDIENCES = ["admins", "costs", "all"] as const;
export type OverdueAudience = (typeof OVERDUE_AUDIENCES)[number];
export const OVERDUE_AUDIENCE_LABEL: Record<OverdueAudience, string> = {
  admins: "Nur Administratoren",
  costs: "Administratoren und Mitarbeiter mit Recht „Kosten“",
  all: "Alle Benutzer des Kunden",
};

export interface OverdueWarningSettings {
  enabled: boolean;
  /** Banner ab so vielen Tagen nach Rechnungsdatum */
  afterDays: number;
  audience: OverdueAudience;
  /** Platzhalter: {anzahl}, {betrag}, {tage} (älteste Rechnung, Tage seit Rechnungsdatum), {pausedatum} */
  text: { de: string; en: string };
  /** Aufträge pausieren ab so vielen Tagen nach Rechnungsdatum (muss später als die Warnung liegen) */
  block: { enabled: boolean; afterDays: number };
  /** Bannertext, solange pausiert ist */
  blockedText: { de: string; en: string };
}

export const DEFAULT_OVERDUE_TEXT = {
  de: "Es gibt {anzahl} überfällige Rechnung(en) über insgesamt {betrag}. Bitte gleicht sie zeitnah aus – andernfalls wird die Weiterverarbeitung eurer Aufträge unterbrochen.",
  en: "There are {anzahl} overdue invoice(s) totalling {betrag}. Please settle them promptly – otherwise processing of your orders will be paused.",
};

export const DEFAULT_OVERDUE_BLOCKED_TEXT = {
  de: "Wegen {anzahl} überfälliger Rechnung(en) über {betrag} ist die Weiterverarbeitung eurer Aufträge pausiert. Neue Aufträge gehen erst nach dem Ausgleich ans Lager – danach automatisch.",
  en: "Because of {anzahl} overdue invoice(s) totalling {betrag}, processing of your orders is paused. New orders go to the warehouse only after payment – automatically.",
};

/** Standard: aus – Logiful schaltet bewusst ein */
export const DEFAULT_OVERDUE_SETTINGS: OverdueWarningSettings = { enabled: false, afterDays: 30, audience: "admins", text: DEFAULT_OVERDUE_TEXT, block: { enabled: false, afterDays: 45 }, blockedText: DEFAULT_OVERDUE_BLOCKED_TEXT };

const textSchema = z.string().trim().min(10).max(600);
const DEFAULT_OVERDUE_SETTINGS_BLOCK = { enabled: false, afterDays: 45 };
const blockSchema = z.object({ enabled: z.boolean(), afterDays: z.number().int().min(1).max(365) });
export const overdueSettingsSchema = z
  .object({
    enabled: z.boolean(),
    afterDays: z.number().int().min(1).max(365),
    audience: z.enum(OVERDUE_AUDIENCES),
    text: z.object({ de: textSchema, en: textSchema }),
    // ältere Einstellungen (vor „Aufträge pausieren“) bekommen den Standard
    block: blockSchema.default(DEFAULT_OVERDUE_SETTINGS_BLOCK),
    blockedText: z.object({ de: textSchema, en: textSchema }).default(DEFAULT_OVERDUE_BLOCKED_TEXT),
  })
  .refine((s) => !s.block.enabled || s.block.afterDays > s.afterDays, { message: "Pausieren muss später als die Warnung beginnen (Vorwarnzeit)." });
/** Ausnahme je Kunde: nur gesetzte Felder weichen vom globalen Standard ab */
export const overdueOverrideSchema = z
  .object({
    enabled: z.boolean().optional(),
    afterDays: z.number().int().min(1).max(365).optional(),
    audience: z.enum(OVERDUE_AUDIENCES).optional(),
    text: z.object({ de: textSchema, en: textSchema }).optional(),
    block: blockSchema.optional(),
    blockedText: z.object({ de: textSchema, en: textSchema }).optional(),
  })
  .strict();
export type OverdueOverride = z.infer<typeof overdueOverrideSchema>;

export function normalizeOverdueSettings(v: unknown): OverdueWarningSettings {
  const p = overdueSettingsSchema.safeParse(v);
  return p.success ? p.data : DEFAULT_OVERDUE_SETTINGS;
}
export function effectiveOverdueSettings(global: unknown, override: unknown): OverdueWarningSettings {
  const g = normalizeOverdueSettings(global);
  const o = overdueOverrideSchema.safeParse(override ?? {});
  return o.success ? { ...g, ...Object.fromEntries(Object.entries(o.data).filter(([, x]) => x !== undefined)) } : g;
}

/** Offene Rechnung aus dem Abrechnungssystem (Stand des letzten Abgleichs) */
export interface OpenInvoice {
  id: number;
  number: string | null;
  date: string;
  gross: number;
  open: number;
  paymentMethod: string | null;
  /** Lastschrift läuft noch bei GoCardless → nicht überfällig */
  debitPending: boolean;
}
export interface OpenInvoicesSnapshot {
  checkedAt: string;
  invoices: OpenInvoice[];
}

const dayMs = 86_400_000;
const ageDays = (date: string, now: Date) => Math.floor((now.getTime() - new Date(`${date}T00:00:00Z`).getTime()) / dayMs);

/** überfällige Rechnungen nach den wirksamen Einstellungen (ohne laufende Lastschriften) */
export function overdueInvoices(snapshot: OpenInvoicesSnapshot | null | undefined, afterDays: number, now = new Date()) {
  const list = (snapshot?.invoices ?? []).filter((i) => !i.debitPending && i.open > 0.005 && ageDays(i.date, now) >= afterDays);
  return {
    invoices: list.map((i) => ({ ...i, days: ageDays(i.date, now) })),
    count: list.length,
    amount: Math.round(list.reduce((s, i) => s + i.open, 0) * 100) / 100,
    oldestDays: list.length ? Math.max(...list.map((i) => ageDays(i.date, now))) : 0,
  };
}

/** Sieht diese Person beim Kunden das Banner? (Logiful-Mitarbeiter immer) */
export function overdueVisibleFor(audience: OverdueAudience, role: string, canViewCosts: boolean): boolean {
  if (role === "staff_admin" || role === "staff") return true;
  if (audience === "all") return true;
  if (role === "customer_admin") return true;
  return audience === "costs" && canViewCosts;
}

export function fillOverdueText(text: string, v: { count: number; amount: string; oldestDays: number; pauseDate?: string }) {
  return text.replace(/\{anzahl\}/g, String(v.count)).replace(/\{betrag\}/g, v.amount).replace(/\{tage\}/g, String(v.oldestDays)).replace(/\{pausedatum\}/g, v.pauseDate ?? "–");
}

/** Haltegrund pausierter Aufträge (Kunde sieht ihn; Handlungsbedarf verlinkt auf die offenen Rechnungen) */
export const BILLING_HOLD = "Offene Rechnungen: Weiterverarbeitung pausiert, bis die Rechnungen ausgeglichen sind";

/**
 * Pause wegen überfälliger Rechnungen: Datum (YYYY-MM-DD), ab dem pausiert wird (älteste offene Rechnung + Tage),
 * und ob das schon erreicht ist. Ohne „Aufträge pausieren“ oder ohne offene Rechnung: null.
 */
export function overdueBlockState(snapshot: OpenInvoicesSnapshot | null | undefined, s: OverdueWarningSettings, now = new Date()): { pauseDate: string; blocked: boolean } | null {
  if (!s.enabled || !s.block.enabled) return null;
  const open = (snapshot?.invoices ?? []).filter((i) => !i.debitPending && i.open > 0.005);
  if (!open.length) return null;
  const first = open.map((i) => new Date(`${i.date}T00:00:00Z`).getTime() + s.block.afterDays * dayMs).sort((a, b) => a - b)[0]!;
  return { pauseDate: new Date(first).toISOString().slice(0, 10), blocked: now.getTime() >= first };
}
